import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
} from '@mui/material';
import { ArrowRight, Play, Check, CircleCheck, Sparkles } from 'lucide-react';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';
import Reveal from './Reveal';
import { tokens } from '../theme';

/* ── Hero conversation card — "Warm Editorial" (Direction B) ── */
function HeroChatCard() {
  return (
    <Box
      aria-label="Example AI conversation"
      sx={{
        bgcolor: tokens.bgPaper,
        border: `1px solid ${tokens.border}`,
        borderRadius: `${tokens.radiusCard}px`,
        boxShadow: tokens.shadowLift,
        p: 3,
        maxWidth: 460,
        ml: 'auto',
        mr: { xs: 'auto', md: 0 },
        position: 'relative',
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          pb: 2,
          mb: 2.5,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: tokens.copperTintStrong,
              color: tokens.copper,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: tokens.fontDisplay,
              fontWeight: 700,
              fontSize: '0.95rem',
              flexShrink: 0,
            }}
          >
            C
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: 1.3,
                color: 'text.primary',
              }}
            >
              CleoLead Assistant
            </Typography>
            <Typography
              sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}
            >
              Instagram DM · Yan Tours
            </Typography>
          </Box>
        </Box>

        {/* live badge — the only animated element (breathe + dot pulse) */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.875,
            bgcolor: tokens.sageTint,
            color: tokens.sage,
            fontSize: '0.8rem',
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            borderRadius: '999px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            animation: 'hero-badge-breathe 3.6s ease-in-out infinite',
            '@keyframes hero-badge-breathe': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-3px)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: tokens.sage,
              animation: 'hero-dot-pulse 2.2s ease-in-out infinite',
              '@keyframes hero-dot-pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.35 },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
          AI on duty 24/7
        </Box>
      </Box>

      {/* customer message — cream bubble */}
      <Box
        sx={{
          maxWidth: '84%',
          mb: 1.75,
          px: 2,
          py: 1.5,
          bgcolor: tokens.bgDefault,
          border: `1px solid ${tokens.border}`,
          color: 'text.primary',
          borderRadius: `${tokens.radiusControl}px`,
          borderBottomLeftRadius: '3px',
          fontSize: '0.875rem',
          lineHeight: 1.5,
        }}
      >
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.5,
            opacity: 0.65,
          }}
        >
          Customer
        </Typography>
        Hi! Looking for a Bali tour, 2 people, next month
      </Box>

      {/* AI reply — sage bubble */}
      <Box
        sx={{
          maxWidth: '84%',
          mb: 1.75,
          ml: 'auto',
          px: 2,
          py: 1.5,
          bgcolor: tokens.sage,
          color: '#FFFFFF',
          borderRadius: `${tokens.radiusControl}px`,
          borderBottomRightRadius: '3px',
          fontSize: '0.875rem',
          lineHeight: 1.5,
        }}
      >
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.5,
            opacity: 0.65,
          }}
        >
          AI · replied in 4s
        </Typography>
        I&rsquo;d love to help plan your Bali trip! Our Discovery package is
        $1,850/person. What dates work?
      </Box>

      {/* booking toast */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mt: 0.75,
          px: 2,
          py: 1.5,
          bgcolor: tokens.sageTint,
          border: '1px solid rgba(47, 93, 80, 0.25)',
          borderRadius: `${tokens.radiusControl}px`,
          color: tokens.sage,
          fontSize: '0.84rem',
          fontWeight: 600,
          lineHeight: 1.4,
        }}
      >
        <CircleCheck size={17} strokeWidth={1.5} style={{ flexShrink: 0 }} />
        Reservation booked — Jun 28, 7:30 PM
      </Box>
    </Box>
  );
}

export default function HeroSection() {
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  const handleAuthClick = () => {
    if (localStorage.getItem('privacy-policy-accepted') === 'true') {
      window.location.href = 'https://app.cleolead.com/login';
      return;
    }
    setPrivacyDialogOpen(true);
  };

  const handlePrivacyAccept = () => {
    setPrivacyDialogOpen(false);
    window.location.href = 'https://app.cleolead.com/login';
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 11 },
          pb: { xs: 9, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal>
                <Typography
                  variant="overline"
                  sx={{ display: 'block', mb: 2 }}
                >
                  AI Business Manager
                </Typography>

                <Typography variant="h1" sx={{ mb: 3 }}>
                  Sells. Supports. Organizes.{' '}
                  <Box
                    component="em"
                    sx={{
                      fontStyle: 'italic',
                      fontWeight: 600,
                      color: tokens.copper,
                      background: `linear-gradient(transparent 68%, ${tokens.copperTintStrong} 68%)`,
                      px: '2px',
                    }}
                  >
                    One AI runs your front office.
                  </Box>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    maxWidth: 520,
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.7,
                  }}
                >
                  AI receptionist that answers your phone, chat AI that knows everything about your business, and a sales agent that follows up 24/7 — across Instagram, WhatsApp, Telegram, web, SMS, and voice. Connect your business in 5 minutes; CleoLead handles the rest.
                </Typography>

                <Box
                  sx={{
                    mb: 4,
                    p: 2,
                    maxWidth: 520,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${tokens.border}`,
                    borderLeft: `3px solid ${tokens.copper}`,
                    background: 'rgba(199, 91, 57, 0.07)',
                  }}
                >
                  <Sparkles
                    size={20}
                    strokeWidth={1.5}
                    color={tokens.copper}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'text.primary' }}>
                    <Box component="strong" sx={{ fontWeight: 600 }}>
                      Plus your own AI business manager.
                    </Box>{' '}
                    Chat with CleoleadAI like any AI assistant you already use — except it
                    actually knows your whole business. Ask for reports, drop in a file, get a
                    daily briefing, or tell it to call a supplier.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowRight size={18} strokeWidth={1.5} />}
                    onClick={handleAuthClick}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Play size={18} strokeWidth={1.5} />}
                    onClick={() => {
                      const el = document.getElementById('how-it-works');
                      el?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    See How It Works
                  </Button>
                </Stack>

                <Stack
                  direction="row"
                  spacing={2.5}
                  sx={{ flexWrap: 'wrap', rowGap: 1 }}
                >
                  {['No credit card', 'Cancel anytime', '5-min setup'].map((t) => (
                    <Stack
                      key={t}
                      direction="row"
                      spacing={0.875}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          color: tokens.sage,
                          flexShrink: 0,
                        }}
                      >
                        <Check size={15} strokeWidth={1.5} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', fontSize: '0.84rem' }}
                      >
                        {t}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Reveal>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Reveal delay={140}>
                <HeroChatCard />
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <PrivacyPolicyDialog
        open={privacyDialogOpen}
        onClose={() => setPrivacyDialogOpen(false)}
        onAccept={handlePrivacyAccept}
      />
    </>
  );
}
